import moment from 'moment';
import { parse } from 'querystring';
import { validationErrors } from './constants/common';
import { ERROR_CODES } from './constants/errorResponseCodes';
import { getCroppedAttendeeImageURL as _getCroppedAttendeeImageURL } from './constants/users';

export function cdnImage(src) {
    if (src.indexOf('/') === 0) {
        src = src.substring(1);
    }
    return `${process.env.REACT_APP_IMAGE_PUBLIC_CDN_URL}${src}`;
}

export function cdnFile(src) {
    if (src.indexOf('/') === 0) {
        src = src.substring(1);
    }
    return `${process.env.REACT_APP_IMAGE_PUBLIC_CDN_URL}${src}`;
}

export function cdnMinifiedImage(src) {
    if (src.indexOf('/') === 0) {
        src = src.substring(1);
    }
    return `${process.env.REACT_APP_IMAGE_MINIFIED_PUBLIC_CDN_URL}${src}`;
}

/**
 * Utility function to pluralize any string based on count
 * @param {Number} count
 * @param {String} noun
 * @param {String} suffix
 */
export const pluralize = (count, noun, suffix = 's') =>
    `${noun}${count !== 1 ? suffix : ''}`;

export const capitalizeFirstLetter = (string) => {
    return string.charAt(0).toUpperCase() + string.slice(1);
};

// https://stackoverflow.com/a/1144249/8578337
// compares if two objects are same or not without going into prototyping
// returns true if two objects are similar otherwise false
export function deepCompareObjects(x, y) {
    var i, l, leftChain, rightChain;

    function compare2Objects(x, y) {
        var p;

        // remember that NaN === NaN returns false
        // and isNaN(undefined) returns true
        if (
            isNaN(x) &&
            isNaN(y) &&
            typeof x === 'number' &&
            typeof y === 'number'
        ) {
            return true;
        }

        // Compare primitives and functions.
        // Check if both arguments link to the same object.
        // Especially useful on the step where we compare prototypes
        if (x === y) {
            return true;
        }

        // Works in case when functions are created in constructor.
        // Comparing dates is a common scenario. Another built-ins?
        // We can even handle functions passed across iframes
        if (
            (typeof x === 'function' && typeof y === 'function') ||
            (x instanceof Date && y instanceof Date) ||
            (x instanceof RegExp && y instanceof RegExp) ||
            (x instanceof String && y instanceof String) ||
            (x instanceof Number && y instanceof Number)
        ) {
            return x.toString() === y.toString();
        }

        // At last checking prototypes as good as we can
        if (!(x instanceof Object && y instanceof Object)) {
            return false;
        }

        if (x.isPrototypeOf(y) || y.isPrototypeOf(x)) {
            return false;
        }

        if (x.constructor !== y.constructor) {
            return false;
        }

        if (x.prototype !== y.prototype) {
            return false;
        }

        // Check for infinitive linking loops
        if (leftChain.indexOf(x) > -1 || rightChain.indexOf(y) > -1) {
            return false;
        }

        // Quick checking of one object being a subset of another.
        // todo: cache the structure of arguments[0] for performance
        for (p in y) {
            if (y.hasOwnProperty(p) !== x.hasOwnProperty(p)) {
                return false;
            } else if (typeof y[p] !== typeof x[p]) {
                return false;
            }
        }

        for (p in x) {
            if (y.hasOwnProperty(p) !== x.hasOwnProperty(p)) {
                return false;
            } else if (typeof y[p] !== typeof x[p]) {
                return false;
            }

            switch (typeof x[p]) {
                case 'object':
                case 'function':
                    leftChain.push(x);
                    rightChain.push(y);

                    if (!compare2Objects(x[p], y[p])) {
                        return false;
                    }

                    leftChain.pop();
                    rightChain.pop();
                    break;

                default:
                    if (x[p] !== y[p]) {
                        return false;
                    }
                    break;
            }
        }

        return true;
    }

    if (arguments.length < 1) {
        return true; //Die silently? Don't know how to handle such case, please help...
        // throw "Need two or more arguments to compare";
    }

    for (i = 1, l = arguments.length; i < l; i++) {
        leftChain = []; //Todo: this can be cached
        rightChain = [];

        if (!compare2Objects(arguments[0], arguments[i])) {
            return false;
        }
    }

    return true;
}

/**
 * A client side validator for email strings
 * @see https://stackoverflow.com/a/46181
 * @param {string} email string to be validated as an email
 */
export const validateEmail = (email) => {
    const re = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    return re.test(String(email).toLowerCase());
};

export const getWindowSize = () => {
    return {
        windowWidth:
            window && window.innerWidth
                ? window.innerWidth
                : document.body.clientWidth,
        windowHeight:
            window && window.innerHeight
                ? window.innerHeight
                : document.body.clientHeight,
    };
};

const createDiv = () => {
    if (!document) {
        return;
    }
    const div = document.createElement('div');
    div.setAttribute(
        'style',
        `font: 12px / 16px NotoSans-SemiBold; visibility: hidden; word-break: break-word;`
    );
    const body = document.querySelector('body');

    body.appendChild(div);

    return div;
};

let div = createDiv();

export const getTextHeight = ({
    containerWidth = 0,
    str = '',
    font = '12px / 16px NotoSans-SemiBold',
}) => {
    if (!div) {
        div = createDiv();
    }

    div.setAttribute(
        'style',
        `width: ${containerWidth}px; font: ${font}; visibility: hidden; word-break: break-word; position: absolute; top: 0; pointer-events: none; white-space: pre-line;`
    );
    div.innerHTML = `<span>${str}</span>`;

    return Math.ceil(div.getBoundingClientRect().height);
};

export const getCroppedAttendeeImageURL = _getCroppedAttendeeImageURL;

export const setWindowFlag = (flagName) => {
    if (window) {
        window.AirmeetFlags = window.AirmeetFlags || {};
        window.AirmeetFlags[flagName] = true;
    }
};

/**
 * Function to return the url params in JS Object structure from the route
 */
export const getUrlParams = (props = {}) => {
    const { onlyString } = props;
    const qs = window.location.search || '?';
    const queryParamString = qs.substring(1);
    if (onlyString) {
        return queryParamString;
    }
    const params = parse(queryParamString);
    return params;
};

/**
 * This function checks if given popup window is closed or not (handles adblocker issues)
 * @param {window} popupWindow popupwindow on which open/close check is done
 */
export const isPopupWindowBlocked = (popupWindow) => {
    return (
        !popupWindow ||
        popupWindow.closed ||
        typeof popupWindow.closed == 'undefined'
    );
};

export const getLocalTimezoneAbbr = (date) => {
    const zoneName = moment.tz.guess();
    let zoneAbbr = moment(date).tz(zoneName).zoneAbbr();
    const NAME_ALPHA_REGEX = /^[A-Z]+$/;
    // Setting offset if not found an abbr for a zone
    if (!NAME_ALPHA_REGEX.test(zoneAbbr)) {
        const offsetValue = moment.tz(zoneName).utcOffset();
        const offsetString = moment.tz(zoneName).format('Z');
        zoneAbbr = `UTC${offsetValue !== 0 ? offsetString : ''}`;
    }
    return zoneAbbr;
};

export const isNumber = (num) => {
    return typeof num === 'number' && !Number.isNaN(num);
};

/**
 * A function that returns a promise that resolves in either true or false
 */
export const urlValidator = async (url) => {
    let module = await import('yup');
    return module.string().trim().url().isValid(url);
};

/**
 * @function getConcatenatedString
 * @param {Array} StringArray Array of Strings to concatenate
 * @param {String} separator Separator to insert between array items
 * @param {String} lastElementSeparator Separator to add before the last item
 * @param {String} endWith Character to end the concatenated string with
 * @returns {String} contatendated string
 */
export const getConcatenatedString = (
    StringArray = [],
    separator = ', ',
    lastElementSeparator = 'and ',
    endWith = '.'
) => {
    return StringArray.join(`${separator} `).replace(
        /, ([^,]*)$/,
        ` ${lastElementSeparator} $1${endWith}`
    );
};

/**
 * @function canShowPremiumFeatures
 * @param {String} subscriptionType Airmeet subscription type
 * @param {Boolean} isTrial Boolean to tell iof Airmeet is in trail mode or not
 * @returns {Boolean} Boolean to tell if premium features can be shown or not
 */
export const canShowPremiumFeatures = (subscriptionType, isTrial) => {
    return (subscriptionType && subscriptionType !== 'FREE') || isTrial
        ? true
        : false;
};

/**
 * Variable to store if the current env is a test env or not.
 */
export const isTestEnv = !['production', 'preprod'].includes(
    process.env.REACT_APP_ENV
);

/**
 * @function getEnvUrl
 * @param {String} url environment url variable
 * @returns {String} Returns url with current domain
 */
export const getEnvUrl = (url) => {
    if (!url || url === '') return url;

    const hostname = window?.location?.hostname || 'airmeet.com';
    if (!hostname.includes('airmeet.com') && !hostname.includes('localhost')) {
        return url.replace('airmeet.com', hostname);
    }

    return url;
};

export const getEmailDomain = (email) => {
    if (!email || !email.includes('@')) return null;
    return email.split('@')[1].toLowerCase();
};

export const validateMaliciousString = ({
    regex = /[<>]+|(&gt|&lt)/i,
    value = '',
    returnError = true,
    key = '',
    localisedError = '',
    showShortError = false,
}) => {
    const isMalicious = typeof value === 'string' && regex.test(value);
    if (returnError) {
        return {
            isMalicious,
            errorData: isMalicious
                ? {
                      [key]: {
                          status: true,
                          msg: localisedError
                              ? localisedError
                              : showShortError
                              ? validationErrors.SMALL_FIELD_ERROR
                              : validationErrors.FIELD_ERROR,
                      },
                  }
                : null,
        };
    }
    return isMalicious;
};

export const isMaliciousStringPresent = (code, errors) => {
    // this is a common function for malicious string BE response valdation
    if (!code || !errors) return;
    if (
        code === ERROR_CODES.MALICIOUS_STRING ||
        (code === ERROR_CODES.SCHEMA_VALIDATION_FAILED &&
            Array.isArray(errors) &&
            errors.length > 0 &&
            errors[0]?.code === ERROR_CODES?.PATTERN)
    ) {
        return true;
    }
    return false;
};

export const getIsDefaultLandingPageBanner = (imageUrl) => {
    return !imageUrl || imageUrl.includes('airmeet-default');
};

export const getEventLandingBannerImgSrc = (imageUrl) => {
    return getIsDefaultLandingPageBanner(imageUrl)
        ? cdnImage('/images/default-landing-banner.png')
        : imageUrl;
};

export const isIntegerBetween = (value, range1, range2) => {
    if (!value || (typeof value !== 'number' && isNaN(value))) return;
    if (value >= range1 && value <= range2) {
        return true;
    }
    return false;
};

export const roundNumber = (num, decimal) => {
    return Number(num.toFixed(decimal));
};

export const ScrollToElement = (element, elementType = 'ref') => {
    if (elementType === 'class') {
        const getElement = document.getElementsByClassName(element);
        if (getElement.length > 0) {
            getElement[0].scrollIntoView({
                behavior: 'smooth',
                block: 'nearest',
                inline: 'nearest',
            });
        }
    } else if (elementType === 'id') {
        const getElement = !!document.getElementById(element);
        if (getElement) {
            getElement.scrollIntoView({
                behavior: 'smooth',
                block: 'nearest',
                inline: 'nearest',
            });
        }
    } else {
        element.scrollIntoView({
            behavior: 'smooth',
            block: 'nearest',
            inline: 'nearest',
        });
    }
};
